import React, { useEffect, useState } from 'react';
import { Grid, Typography, IconButton, Menu, ListItemIcon } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import Item from '../Common/Item';
import {
  FolderWithActivity,
  closeDeleteFolderModalOnSuccess,
  closeRenameFolderOnSuccess,
  deleteFolder,
  saveFolder,
} from '../../redux/activityLibrary/activityLibrarySlice';
import StyledMenuItem from '../Common/StyledMenuItem';
import CustomModal from '../Common/CustomModal';
import DeleteActivityModal from './DeleteActivity';
import { useAppSelector } from '../../redux/hooks';
import CreateRenameFolderModal from './CreateRenameFolderModal/CreateRenameFolderModal';
import { ACTIVITY_LIBRARY_ROUTE } from '../../utils/routes';
import createPermissionCheckers from '../../permissions-validation/createPermissionCheckers';
import { UserPermissionType } from '../../types/commonTypes';

export function displaySentence(sentence: string): string {
  if (sentence.length > 30) {
    return `${sentence.substring(0, 30)}...`;
  }
  return sentence;
}

function Folder({ folder }: { folder: FolderWithActivity }) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const { isRenameFolderSuccess } = useAppSelector((state) => state.activityLibrary);
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const coachId = useAppSelector((state) => state.coach.coach?.id);
  const permissionCheckers = createPermissionCheckers();
  const { checkPermissions } = permissionCheckers;
  const { isDeleteFolderSuccess, deleteFolderLoading } = useAppSelector(
    (state) => state.activityLibrary
  );

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleCloseModal = () => {
    setIsDeleteModalOpen(false);
  };

  const handleDelete = () => {
    if (!coachId) return;
    dispatch(deleteFolder({ folderId: folder.id, coachId }));
  };

  const handleClick = (event: React.MouseEvent<HTMLDivElement | HTMLButtonElement>) => {
    if (
      event.target instanceof HTMLElement &&
      (event.target.tagName === 'DIV' ||
        event.target.tagName === 'IMG' ||
        event.target.tagName === 'SPAN')
    ) {
      dispatch(saveFolder(folder));
    } else {
      setAnchorEl(event.currentTarget);
    }
  };

  useEffect(() => {
    if (isDeleteFolderSuccess) {
      handleCloseModal();
      dispatch(closeDeleteFolderModalOnSuccess());
    }
  }, [isDeleteFolderSuccess]);

  useEffect(() => {
    if (isRenameFolderSuccess) {
      setIsRenameModalOpen(false);
      dispatch(closeRenameFolderOnSuccess());
    }
  }, [isRenameFolderSuccess]);

  const handleRenameFolder = () => {
    handleClose();
    setIsRenameModalOpen(true);
  };

  const handleDismissRenameFolder = () => {
    setIsRenameModalOpen(false);
  };

  return (
    <Grid item xs={4}>
      <Item sx={{ cursor: 'pointer' }} onClick={handleClick}>
        <Grid display="flex" alignItems="center" gap="10px">
          <img src="/assets/icons/activity-folder.svg" alt="activity-folder" />
          <Typography variant="bodyBold" color="primary">
            {displaySentence(folder.name)}
          </Typography>
        </Grid>
        {location.pathname === ACTIVITY_LIBRARY_ROUTE &&
          checkPermissions(
            [
              UserPermissionType.ACTIVITY_LIBRARY_MODULE_UPDATE_ACTIVITY_LIBRARY,
              UserPermissionType.ACTIVITY_LIBRARY_MODULE_DELETE_ACTIVITY_LIBRARY,
            ],
            false
          ) && (
            <IconButton>
              <MoreVertIcon style={{ color: '#7CAEBE' }} />
            </IconButton>
          )}
      </Item>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {checkPermissions([UserPermissionType.ACTIVITY_LIBRARY_MODULE_UPDATE_ACTIVITY_LIBRARY]) && (
          <StyledMenuItem onClick={handleRenameFolder}>
            <ListItemIcon>
              <img src="/assets/icons/edit-pen.svg" alt="edit-pen" />
            </ListItemIcon>
            <Typography variant="bodyRegular" color="primary">
              {t('activityLibrary.filled.rename')}
            </Typography>
          </StyledMenuItem>
        )}

        {checkPermissions([UserPermissionType.ACTIVITY_LIBRARY_MODULE_DELETE_ACTIVITY_LIBRARY]) && (
          <StyledMenuItem
            onClick={() => {
              handleClose();
              setIsDeleteModalOpen(true);
            }}
          >
            <ListItemIcon>
              <img src="/assets/icons/trash-bin-red.svg" alt="edit-pen" />
            </ListItemIcon>
            <Typography variant="bodyRegular" color="primary">
              {t('activityLibrary.filled.delete')}
            </Typography>
          </StyledMenuItem>
        )}
      </Menu>
      {isDeleteModalOpen && (
        <CustomModal isOpen={isDeleteModalOpen} xs={3.3}>
          <DeleteActivityModal
            handleDelete={handleDelete}
            handleDismiss={handleCloseModal}
            activityName={folder.name}
            loading={deleteFolderLoading}
            loadingText={t('activityLibrary.filled.deletingFolder')}
          />
        </CustomModal>
      )}
      <CreateRenameFolderModal
        isCreateFolder={false}
        initialFolderName={folder.name}
        isOpen={isRenameModalOpen}
        id={folder.id}
        handleDismiss={handleDismissRenameFolder}
      />
    </Grid>
  );
}

export default Folder;
